import React from "react";
import { useTranslation } from "react-i18next";
import { etiquetaPeriodicidad } from "../periodicidad";
import { imagenDeRespaldo } from "../imagenDeRespaldo";
import {
  Modal,
  Typography,
  Tag,
  Button,
  Card,
  Space,
  Divider,
  Popconfirm,
  Tooltip,
  Empty,
  Spin,
} from "antd";
import {
  ClockCircleOutlined,
  DeleteOutlined,
  EyeOutlined,
  UserOutlined,
  TeamOutlined,
  ArrowUpOutlined,
} from "@ant-design/icons";
import "./TherapyDetailModal.scss";

const { Title, Paragraph, Text } = Typography;

export function TherapyDetailModal({
  isOpen,
  onClose,
  therapy,
  loading,
  onRemoveStep,
  onViewScene,

  // ✅ NUEVO: mover paso arriba (swap con el anterior)
  // Si no se pasa, no se muestra el botón (no rompe nada)
  onMoveStepUp,
}) {
  // El namespace se declara una vez aqui: i18next solo carga el fichero que
  // necesita esta pantalla. El hook va ANTES del early return.
  const { t } = useTranslation("rooms");

  if (!isOpen) return null;

  const renderContent = () => {
    if (loading && !therapy) {
      return (
        <div className="loading-state">
          <Spin size="large" />
        </div>
      );
    }

    if (!therapy)
      return <Empty description={t("detalleTerapia.sinInformacion")} />;

    const {
      title,
      description,
      objectives,
      script,
      visibility,
      categories = [],
      scenes = [],
      image,
    } = therapy;

    const FALLBACK_IMG = imagenDeRespaldo(t("detalleSala.sinImagen"));

    const canReorder = typeof onMoveStepUp === "function";

    return (
      <div className="therapy-detail-layout">
        {/* COLUMNA IZQUIERDA */}
        <div className="detail-left">
          <div className="cover-wrapper">
            <img
              src={image || FALLBACK_IMG}
              alt={title}
              onError={(e) => {
                e.currentTarget.src = FALLBACK_IMG;
              }}
            />
            <div className="vis-badge">
              {visibility === "private" ? (
                <Tag icon={<UserOutlined />}>{t("detalleTerapia.privada")}</Tag>
              ) : (
                <Tag color="blue" icon={<TeamOutlined />}>
                  {t("detalleTerapia.compartida")}
                </Tag>
              )}
            </div>
          </div>

          <div className="meta-section">
            <Title level={4}>{title}</Title>

            <div className="tags-row">
              {categories.map((cat) => (
                <Tag key={cat._id} color={cat.colorCode}>
                  {cat.title}
                </Tag>
              ))}
            </div>

            <Divider style={{ margin: "16px 0" }} />

            <div className="text-block">
              <Text strong>{t("detalleTerapia.descripcionGeneral")}</Text>
              <Paragraph
                type="secondary"
                ellipsis={{ rows: 3, expandable: true }}
              >
                {description}
              </Paragraph>
            </div>

            <div className="text-block">
              <Text strong>{t("detalleTerapia.objetivos")}</Text>
              <Paragraph className="clinical-text">{objectives}</Paragraph>
            </div>

            <div className="text-block">
              <Text strong>{t("detalleTerapia.guion")}</Text>
              <div className="script-box">
                <Paragraph>{script}</Paragraph>
              </div>
            </div>
          </div>
        </div>

        {/* COLUMNA DERECHA */}
        <div className="detail-right">
          <div className="itinerary-header">
            <Title level={5}>
              {t("detalleTerapia.itinerarioTitulo", { count: scenes.length })}
            </Title>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t("detalleTerapia.secuenciaIntervencion")}
            </Text>
          </div>

          <div className="scenes-list-wrapper">
            {scenes.length > 0 ? (
              <Space orientation="vertical" style={{ width: "100%" }} size={12}>
                {scenes.map((step, index) => {
                  const {
                    virtualScene,
                    customDescription,
                    periodicity,
                    _id: stepId,
                  } = step;

                  const sceneImg =
                    virtualScene?.module_01?.bannertop?.url || FALLBACK_IMG;

                  return (
                    <Card key={stepId} className="scene-step-item" size="small">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 12,
                        }}
                      >
                        <div className="step-number">{index + 1}</div>

                        <div className="step-thumb">
                          <img
                            src={sceneImg}
                            alt="scene"
                            onError={(e) => {
                              e.currentTarget.src = FALLBACK_IMG;
                            }}
                          />
                        </div>

                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            className="step-header"
                            style={{
                              display: "flex",
                              gap: 10,
                              alignItems: "center",
                              flexWrap: "wrap",
                            }}
                          >
                            <Text strong>
                              {virtualScene?.module_01?.ModOneTitle ||
                                t("detalleTerapia.salaSinTitulo")}
                            </Text>

                            {periodicity && (
                              <Tag
                                icon={<ClockCircleOutlined />}
                                className="period-tag"
                              >
                                {etiquetaPeriodicidad(t, periodicity)}
                              </Tag>
                            )}
                          </div>

                          <div className="step-desc" style={{ marginTop: 6 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {customDescription ||
                                t("detalleTerapia.sinInstrucciones")}
                            </Text>
                          </div>
                        </div>

                        <div
                          className="step-actions"
                          style={{ display: "flex", gap: 8 }}
                        >
                          {/* ✅ Subir: solo desde la 2ª sala y solo si hay handler */}
                          {canReorder && index > 0 && (
                            <Tooltip title={t("detalleTerapia.moverArriba")}>
                              <Button
                                type="text"
                                icon={<ArrowUpOutlined />}
                                onClick={() => onMoveStepUp(stepId)}
                              />
                            </Tooltip>
                          )}

                          <Tooltip title={t("detalleTerapia.verDetalleSala")}>
                            <Button
                              type="text"
                              icon={<EyeOutlined />}
                              onClick={() => onViewScene(virtualScene)}
                            />
                          </Tooltip>

                          <Popconfirm
                            title={t("detalleTerapia.eliminarPasoTitulo")}
                            description={t(
                              "detalleTerapia.eliminarPasoDescripcion",
                            )}
                            onConfirm={() => onRemoveStep(stepId)}
                            okText={t("acciones.si")}
                            cancelText={t("acciones.no")}
                          >
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                            />
                          </Popconfirm>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </Space>
            ) : (
              <div className="empty-itinerary">
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={t("detalleTerapia.sinSesiones")}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      width={1000}
      footer={null}
      centered
      className="revi-therapy-detail-modal"
      destroyOnHidden
    >
      {renderContent()}
    </Modal>
  );
}
